import { useTranslate } from "@similarweb/i18n-provider";
import type { ColumnsType } from "@similarweb/pro-logic-components";
import { Table } from "@similarweb/pro-logic-components";
import { Button, Card } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import type { ICategory, ICustomCategory } from "app/common/services/categoryService.types";
import { allTrackers } from "app/services/track/track";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { CONVERSION_RATE_TRANSLATION_KEYS } from "../../consts";
import { ConversionRateCardHeader } from "../../shared/ConversionRateCardHeader";

const LEADERS_LOADER_ROW_COUNT = 5;

interface IConversionRateLeadersTableProps<TRow> {
    automationId: string;
    title: string;
    tooltip: string;
    dateRangeText: string;
    countryCode: number;
    countryText: string;
    isLoading: boolean;
    rows: TRow[];
    columnConfig: ColumnsType<TRow>;
    trackingSubName: string;
    primaryCategory: ICategory | ICustomCategory;
}

export const ConversionRateLeadersTable = <TRow extends object>({
    automationId,
    title,
    tooltip,
    dateRangeText,
    countryCode,
    countryText,
    isLoading,
    rows,
    columnConfig,
    trackingSubName,
    primaryCategory,
}: IConversionRateLeadersTableProps<TRow>) => {
    const { translate } = useTranslate();

    const handleExploreClick = (): void => {
        allTrackers.dispatchTrackEvent("Button", "click", `Explore all players/${trackingSubName}`, {
            industry_type: primaryCategory.isCustomCategory ? "custom" : "predefined",
            industry_name: primaryCategory.name ?? primaryCategory.text,
        });
        void proNavigator.go("market_research_players", proNavigator.getParams());
    };

    return (
        <Card
            data-automation={automationId}
            flex="1"
            minWidth="0"
        >
            <ConversionRateCardHeader
                title={title}
                tooltip={tooltip}
                dateRangeText={dateRangeText}
                countryCode={countryCode}
                countryText={countryText}
                automationPrefix={automationId}
            />
            <Table
                theme="white"
                sticky={false}
                data={rows}
                columnConfig={columnConfig}
                loaderRowCount={LEADERS_LOADER_ROW_COUNT}
                isLoading={isLoading}
                bordersConfig={{ body: false, header: false }}
            />
            {!isLoading ? (
                <Flex
                    justifyContent="flex-end"
                    padding="8px 16px"
                >
                    <Button
                        type="flat"
                        data-automation={`${automationId}-explore-cta`}
                        onClick={handleExploreClick}
                    >
                        {translate(CONVERSION_RATE_TRANSLATION_KEYS.leadersExploreAllCta)}
                    </Button>
                </Flex>
            ) : null}
        </Card>
    );
};
